import { TEMPLATE_TYPES_VALUE } from 'src/app/constants/internationalized-constants-en'
import { LanguageDictionaryHandlingService } from '../../tree/services/language-dictionary-handling/language-dictionary-handling.service'
import { EvirLanguageDictionary, FormDataModel, InspectionDetailModel, TranslationObject, ZoneLayoutModel } from '../../tree/models/tree.model'

export class TemplateLibraryPieceModel {
  id: string
  pieceType: TEMPLATE_TYPES_VALUE
  name: string
  admin: boolean
  created: string
  piece:
    | ZoneLayoutModel
    | FormDataModel
    | InspectionDetailModel
}

export class DataConfigurationPieceModel {
  name: string
  pieceName: string
  data: DataConfigurationPieceModel[]
  value?: any
}

export const blankPieces = (
  languageDictionaryService: LanguageDictionaryHandlingService,
  langDict: EvirLanguageDictionary,
  translationObject: TranslationObject = {}
) => {
  const preTripLangKey = languageDictionaryService.getOrCreateLangKey(
    'Pre-Trip',
    translationObject,
    langDict
  )
  const getLangKey = value => languageDictionaryService.getLangKeyByString(value, langDict)

  return [
    {
      pieceType: TEMPLATE_TYPES_VALUE.CONFIGURATION,
      name: '(Blank Configuration)',
      piece: {
        assetType: ['vehicle', 'standard'],
        components: [
          {
            maxSeverity: 255,
            minSeverity: 0,
            componentLangKey: getLangKey('(Blank Component)'),
            suggestedConditionLangKeys: [getLangKey('(Blank Condition)')],
          },
        ],
        configZones: [
          {
            tagComponents: [0],
            tagLangKey: getLangKey('(Blank Zone)'),
            tagNumber: 1,
            tagTypeLangKey: getLangKey('zone'),
            zoneInspectionTypes: [preTripLangKey],
          },
        ],
        zoneLayoutLangKey: getLangKey('(Blank Configuration)'),
      },
    },
    {
      name: 'CHECKBOX',
      pieceType: TEMPLATE_TYPES_VALUE.FORM,
      piece: {
        fieldNameLangKey: '',
        hintLangKey: '',
        inspectionDetailType: 'bool',
        inspectionDetailCategory: 'other',
        required: false,
        default: false,
      },
    },
    {
      name: 'DROPDOWN_SELECT_FIELD',
      pieceType: TEMPLATE_TYPES_VALUE.FORM,
      piece: {
        fieldNameLangKey: '',
        hintLangKey: '',
        inspectionDetailType: 'select',
        inspectionDetailCategory: 'other',
        required: false,
        selectLangKeys: [],
      },
    },
    {
      name: 'TEXT_FIELD',
      pieceType: TEMPLATE_TYPES_VALUE.FORM,
      piece: {
        fieldNameLangKey: '',
        hintLangKey: '',
        inspectionDetailType: 'text',
        required: false,
        inspectionDetailCategory: 'other',
        regex: `/[A-Za-z0-9 ''.-@$#]*/`,
        minimum: 0,
        maximum: 60,
      },
    },
    {
      name: 'CAMERA_PHOTOS',
      pieceType: TEMPLATE_TYPES_VALUE.FORM,
      piece: {
        fieldNameLangKey: '',
        hintLangKey: '',
        inspectionDetailCategory: 'other',
        inspectionDetailType: 'photo',
        required: false,
      },
    },
    {
      pieceType: TEMPLATE_TYPES_VALUE.INSPECTION_TYPE,
      name: 'Pre-Trip',
      piece: {
        formData: [
          {
            hintLangKey: getLangKey('Value reported on the odometer'),
            maximum: 1000000000,
            minimum: 0,
            required: true,
            fieldNameLangKey: getLangKey('Odometer'),
            inspectionDetailType: 'int',
            inspectionDetailCategory: 'odometer',
            inspectionDetailMeasurementType: 'distance',
            inspectionDetailUnitConversionPair: ['mi', 'km'],
          },
          {
            hintLangKey: getLangKey('Engine hours as reported on dash'),
            maximum: 1000000000,
            minimum: 0,
            required: false,
            fieldNameLangKey: getLangKey('Engine Hours'),
            inspectionDetailType: 'int',
            inspectionDetailCategory: 'engineHours',
            inspectionDetailMeasurementType: 'time',
            inspectionDetailUnitConversionPair: ['hrs'],
          },
          {
            hintLangKey: getLangKey('Estimated fuel in tank'),
            maximum: 500,
            minimum: 0,
            required: false,
            fieldNameLangKey: getLangKey('Fuel Level'),
            inspectionDetailType: 'int',
            inspectionDetailCategory: 'fuelVolume',
            inspectionDetailMeasurementType: 'fluidVolume',
            inspectionDetailUnitConversionPair: ['gal', 'l'],
          },
          {
            regex: `/[A-Za-z0-9 ''.-@$#]*/`,
            hintLangKey: getLangKey('Shipping Doc #'),
            maximum: 50,
            required: false,
            fieldNameLangKey: getLangKey('Shipping Doc #'),
            inspectionDetailType: 'text',
            inspectionDetailCategory: 'other',
          },
          {
            hintLangKey: getLangKey('Route number'),
            maximum: 1000,
            minimum: 0,
            required: false,
            fieldNameLangKey: getLangKey('Route Number'),
            inspectionDetailType: 'int',
            inspectionDetailCategory: 'other',
          },
          {
            default: false,
            hintLangKey: getLangKey('Do you have hazardous material on board'),
            required: true,
            fieldNameLangKey: getLangKey('Hazmat'),
            inspectionDetailType: 'bool',
            inspectionDetailCategory: 'other',
          },
          {
            default: false,
            hintLangKey: getLangKey('Is your vehicle or load over-sized'),
            required: true,
            fieldNameLangKey: getLangKey('Oversized Vehicle'),
            inspectionDetailType: 'bool',
            inspectionDetailCategory: 'other',
          },
        ],
        inspectionDetailLangKey: getLangKey('Pre-Trip'),
        inspectionDescriptionLangKey: getLangKey('Inspect the vehicle before driving according to DOT and company policy.'),
        inspectionType: 'Pre-Trip',
        enforceOrder: true,
        requiresReview: true,
        legallyMandated: true,
        prepopulateDefects: true,
        associateDriver: true,
        zoneless: false,
      },
    },
    {
      pieceType: TEMPLATE_TYPES_VALUE.INSPECTION_TYPE,
      name: 'Post-Trip',
      piece: {
        formData: [
          {
            hintLangKey: getLangKey('Value reported on the odometer'),
            maximum: 1000000000,
            minimum: 0,
            required: true,
            fieldNameLangKey: getLangKey('Odometer'),
            inspectionDetailType: 'int',
            inspectionDetailCategory: 'odometer',
            inspectionDetailMeasurementType: 'distance',
            inspectionDetailUnitConversionPair: ['mi', 'km'],
          },
          {
            hintLangKey: getLangKey('Engine hours as reported on dash'),
            maximum: 1000000000,
            minimum: 0,
            required: false,
            fieldNameLangKey: getLangKey('Engine Hours'),
            inspectionDetailType: 'int',
            inspectionDetailCategory: 'engineHours',
            inspectionDetailMeasurementType: 'time',
            inspectionDetailUnitConversionPair: ['hrs'],
          },
          {
            hintLangKey: getLangKey('Estimated fuel in tank'),
            maximum: 500,
            minimum: 0,
            required: false,
            fieldNameLangKey: getLangKey('Fuel Level'),
            inspectionDetailType: 'int',
            inspectionDetailCategory: 'fuelVolume',
            inspectionDetailMeasurementType: 'fluidVolume',
            inspectionDetailUnitConversionPair: ['gal', 'l'],
          },
          {
            regex: `/[A-Za-z0-9 ''.-@$#]*/`,
            hintLangKey: getLangKey('Trip notes or concerns'),
            maximum: 500,
            required: false,
            fieldNameLangKey: getLangKey('Post trip notes'),
            inspectionDetailType: 'text',
            inspectionDetailCategory: 'other',
          },
        ],
        inspectionDetailLangKey: getLangKey('Post-Trip'),
        inspectionDescriptionLangKey: getLangKey('Inspect the vehicle after driving according to DOT and company policy.'),
        inspectionType: 'Post-Trip',
        enforceOrder: true,
        requiresReview: true,
        legallyMandated: true,
        prepopulateDefects: true,
        zoneless: false,
        associateDriver: true,
      },
    },
    {
      pieceType: TEMPLATE_TYPES_VALUE.INSPECTION_TYPE,
      name: 'Time Card',
      piece: {
        formData: [
          {
            selectLangKeys: ['In', 'Out'].map(x => getLangKey(x)),
            hintLangKey: getLangKey('Punch time card in or out'),
            required: true,
            fieldNameLangKey: getLangKey('Clock'),
            inspectionDetailType: 'select',
            inspectionDetailCategory: 'other',
          },
          {
            hintLangKey: getLangKey('Route number'),
            maximum: 1000,
            minimum: 0,
            required: false,
            fieldNameLangKey: getLangKey('Route Number'),
            inspectionDetailType: 'int',
            inspectionDetailCategory: 'other',
          },
          {
            selectLangKeys: ['Regular', 'Special', 'Field Trip', 'Charter', 'Athletic']
              .map(x => getLangKey(x)),
            hintLangKey: getLangKey('Route Type'),
            required: false,
            fieldNameLangKey: getLangKey('Route Type'),
            inspectionDetailType: 'select',
            inspectionDetailCategory: 'other',
          },
        ],
        inspectionDetailLangKey: getLangKey('Time Card'),
        inspectionDescriptionLangKey: getLangKey('Record your time by clocking in or out.'),
        inspectionType: 'Time Card',
        enforceOrder: false,
        requiresReview: false,
        legallyMandated: false,
        prepopulateDefects: true,
        zoneless: true,
        associateDriver: true,
      },
    },
    {
      pieceType: TEMPLATE_TYPES_VALUE.INSPECTION_TYPE,
      name: 'Load Securement',
      piece: {
        inspectionDetailLangKey: getLangKey('Load Securement'),
        inspectionDescriptionLangKey: getLangKey('Inspect tarps, straps, and other load safety devices.'),
        inspectionType: 'Load Securement',
        enforceOrder: false,
        requiresReview: false,
        legallyMandated: false,
        prepopulateDefects: true,
        associateDriver: true,
        zoneless: false,
      },
    },
    {
      pieceType: TEMPLATE_TYPES_VALUE.INSPECTION_TYPE,
      name: 'Child Check',
      piece: {
        inspectionDetailLangKey: getLangKey('Child Check'),
        inspectionDescriptionLangKey: getLangKey('Check behind and under seats for any remaining children.'),
        inspectionType: 'Child Check',
        enforceOrder: true,
        requiresReview: false,
        legallyMandated: false,
        prepopulateDefects: true,
        zoneless: true,
        associateDriver: true,
      },
    },
  ] as TemplateLibraryPieceModel[]
}

export class RawTemplateLibraryPieceModel {
  pieceType: TEMPLATE_TYPES_VALUE
  name: string
  piece: ZoneLayoutModel | FormDataModel
}
